import type { BotPromptChannel } from "../Bot/BotPrompt.Interface";
import type { BotSequenceConfigStep } from "../BotSequences/BotSequenceConfig.Interfaces";
import type { CallScriptStep, CallScriptStepSnapshot } from "../CallScript/CallScript.Interface";
import type { Domain } from "../Domain/Domain.Data";
import type { BaseEvalCase, EvalCriterion, EvalTurn } from "../Evals/BaseEvalCase";
import { KNOWN_TOOL_NAMES, TOOL_NAMED_CRITERION_TYPES } from "../Evals/CriterionRegistry";
import type { RoutingGroupInterface } from "../RoutingGroup/RoutingGroup.Interface";
import type { StorageRoutingTopic } from "../RoutingGroup/StorageRoutingTopic";
import type { LocationVarKey } from "../Variables/Generated/LocationVarKey.Data";
import type { WidgetInterface } from "../Widget/Widget.Interface";

/**
 * Subset of widget-config fields the eval harness can project onto an eval
 * conversation at create time. Mirrors what production passes to
 * `ChatConversationService.create` from a real widget session.
 *
 * Per M0b (docs/snowy/widget/2026-05-21-m0-conversation-focus-and-group-lock.md):
 *   - `locationId`                       → BotConversation.focusLocationId
 *   - `groupId` (when
 *     `includeOnlyGroupLocations`)       → BotConversation.limitToGroupId
 * Tools must honor `limitToGroupId` for every location-returning surface.
 */
export type WidgetEvalConfig = Pick<WidgetInterface, "groupId" | "locationId" | "includeOnlyGroupLocations">;

/**
 * Per-routing-topic routing-group setting overrides an eval case pins
 * (ENG-3089). At run time the driver upserts a LOCATION-scope routing group
 * row on the eval location owning each listed topic with these fields (the
 * narrowest scope wins in `ResolveRulesService`), and restores the previous
 * state in the run's finally block — the same declarative override/restore
 * handshake `scheduleOverrides` uses. Absence = whatever the eval org's
 * groups say (platform defaults on a fresh org).
 */
export interface RoutingGroupEvalOverrideFields {
  requiredContactInfo?: RoutingGroupInterface["requiredContactInfo"];
  contactRefusalBehavior?: RoutingGroupInterface["contactRefusalBehavior"];
}
export type RoutingGroupEvalOverrides = Partial<Record<StorageRoutingTopic, RoutingGroupEvalOverrideFields>>;

/**
 * Per-case call-script overrides (ENG-3356): the scripts written as
 * LOCATION-scope `call_script` rows on the eval location for the run, keyed by
 * slug, restored in the finally block. `steps: []` removes an inherited script
 * (the platform "sales-call" included) at that location.
 */
export interface CallScriptEvalOverride {
  name: string;
  whenToUse: string;
  steps: CallScriptStep[];
  /** "For sequences only" (ENG-3403). */
  forSequencesOnly?: boolean;
}
export type CallScriptEvalOverrides = Record<string, CallScriptEvalOverride>;

/**
 * Per-case sequence ladder overrides (ENG-3403): keyed by `BotSequenceType`, each value is the
 * whole ladder written as the LOCATION-scope `bot_sequence_config` row on the eval location for
 * the run (restored in the finally block). Used with `priorOutboundContext.metadata.sequenceStepKey`
 * to make the eval conversation "come from" a day whose `callScriptSlug` names a script.
 */
export type SequenceLadderEvalOverrides = Record<string, BotSequenceConfigStep[]>;

/**
 * Per-case step-library overrides (ENG-3402): keyed by step slug, each value is written as the
 * ORG-scope `call_script_step` row on the eval org for the run (restored in the finally block).
 * A platform slug overrides that step; an `org-…` slug adds a step scripts can reference.
 */
export type CallScriptStepEvalOverrides = Record<string, CallScriptStepSnapshot>;

/**
 * Canonical eval-fixture roles. These mirror the roles the bot's DataCopier
 * creates when seeding the eval org. Studio dropdowns use these so authors
 * can't typo a role that doesn't resolve.
 *
 * Mirror these in `apps/bot/src/Evals/DataCopier.ts` if new roles are added.
 */
export const LOCATION_FIXTURE_ROLES = ["standard", "minimal", "full-featured", "rv-boat"] as const;
export type LocationFixtureRole = (typeof LOCATION_FIXTURE_ROLES)[number];

export const USER_FIXTURE_ROLES = [
  "prospect",
  "existing-tenant",
  "multi-lease",
  "past-due",
  "returning",
  // Branch-specific roles for stories whose criterion would otherwise have to
  // detect tool-result branches and apply different acceptance rules. Each
  // story that splits on a deterministic data condition gets its own pair.
  "existing-tenant-with-gate-code",
  "existing-tenant-no-gate-code",
  "existing-tenant-on-autopay",
  "existing-tenant-not-on-autopay",
  // ENG-3089 contact-gate fixtures: a caller the system knows NOTHING about.
  // `anonymous-web-visitor` — no name, no phone, no email (an anonymous chat
  // widget session). `unknown-caller` — phone only (caller ID on voice/SMS),
  // no name, no email. Both always synthetic.
  "anonymous-web-visitor",
  "unknown-caller"
] as const;
export type UserFixtureRole = (typeof USER_FIXTURE_ROLES)[number];

/**
 * Lifecycle state for a user story.
 *
 *  - backlog: Not yet committed to build. Eval failures are silenced — they're
 *    aspirational, not regressions.
 *  - building: Actively under construction. Eval failures DO count toward the
 *    suite signal — the whole point of building is iterating against real
 *    eval feedback.
 *  - live: Shipped + eval passing. Failures are regressions.
 *  - deprecated: Retired; skipped entirely.
 *
 * For the harness, `building` and `live` are "real" — failures break the run.
 * `backlog` and `deprecated` are silenced. See `toFeatureStatus()` below.
 */
export type UserStoryStatus = "backlog" | "building" | "live" | "deprecated";

/**
 * The eval test embedded in a user story. Optional — stories without an eval
 * are first-class citizens (they're the proposed/scoping ones).
 */
/**
 * A single step in an `E2EEvalSpec`. Maps to one or more chrome-devtools MCP
 * tool invocations (e.g. click, type, take_snapshot) followed by free-text
 * assertions the agent verifies before moving on.
 */
export interface E2EEvalStep {
  /** What the agent does — free-text instruction. Should map to chrome-devtools
   *  MCP tools (mcp__chrome-devtools__click / type_text / take_snapshot / etc.). */
  action: string;
  /** What to verify after the action — each entry is a free-text assertion
   *  the agent confirms (via take_snapshot, list_network_requests,
   *  evaluate_script, etc.). */
  assertions: string[];
}

/**
 * Browser-level eval spec — sibling to the conversation `eval` field on
 * `UserStory`. Drives chrome-devtools-MCP-based E2E tests for behaviors the
 * conversation eval can't observe (widget rendering, transcript collapse,
 * client-side network events). The "runner" is not a CI process — it's an
 * agent (Claude Code) following the documented protocol in
 * docs/snowy/guides/e2e-protocol.md.
 *
 * Plan: docs/snowy/plans/2026-05-10-storage-stories-fix-plan.md §B2.
 */
export interface E2EEvalSpec {
  /** 1-line summary of what this E2E verifies. */
  goal: string;
  /** URL the agent loads to drive the test (template tokens supported). */
  url: string;
  /** Optional: user chat messages sent before the E2E steps begin to set
   *  up picker / widget context. Delivered through the same conversation
   *  flow as a normal session. */
  setupMessages?: string[];
  /** Ordered E2E steps. Each = an action plus assertions. */
  steps: E2EEvalStep[];
  /** Final assertions evaluated at the end of the run (e.g. "no
   *  POST to /api/chat/list-units happened at any point"). */
  finalAssertions?: string[];
}

/**
 * Prior outbound context injected into a conversation before the first user
 * turn fires. Used by Outbound (Sequence) Response stories (us-110..us-118)
 * to simulate "user is replying to a message the bot already sent" —
 * the eval framework would otherwise enter the case with no assistant turn,
 * leaving the bot to interpret the user's reply with no anchor.
 *
 * The bot's first call sees this assistant message in its history exactly as
 * if it had been a real outbound. The message is also persisted to the DB
 * (via BotMessageService.saveAssistantMessage with metadata tagging it as an
 * outbound context) so it appears in the admin transcript view.
 *
 * Plan: docs/snowy/plans/2026-05-10-storage-stories-fix-plan.md §B1.
 */
export interface PriorOutboundContext {
  /** Channel the outbound was sent on. Today informational; used by admin view + future channel-specific behavior. */
  kind: "sms-outbound" | "voice-outbound" | "email-outbound";
  /** The exact assistant turn that was sent. Template tokens `{{...}}` from TurnVariables.ts are supported. */
  assistantTurn: string;
  /** Optional metadata for admin observability (campaign id, sequence id, outbound reason). */
  metadata?: {
    campaignId?: string;
    sequenceId?: string;
    outboundReason?: string;
    /**
     * The ladder day this outbound "was" (`BotSequenceConfigStep.key`, ENG-3403). Stamped on the
     * eval conversation as `originatingSequenceStepId`, exactly as a real fire does, so the prompt
     * starts with the day's bound script.
     */
    sequenceStepKey?: string;
  };
}

export interface UserStoryEvalSpec {
  /** Conversation turns the eval driver runs */
  turns: EvalTurn[];

  /** What the judge checks after the conversation */
  criteria: EvalCriterion[];

  /** Optional context for the judge */
  judgeContext?: string;

  /** Which fixture location to use (from TestLocations.ts in the bot) */
  locationFixture?: string;

  /** Which fixture user to use (from TestUsers.ts in the bot) */
  userFixture?: string;

  /** Location-variable overrides applied for this case */
  locationConfig?: Partial<Record<LocationVarKey, string>>;

  /**
   * Per-slug Schedule overrides applied for this case. `"clear"` deletes
   * the location-scope Schedule row so the bot reads "(not configured)" —
   * use it to test the "hours data missing" path against the structured
   * Schedule model (legacy `locationConfig` overrides on the deprecated
   * Cortex hours vars no longer reach the bot's read path).
   */
  scheduleOverrides?: Partial<Record<"office-hours" | "gate-access" | "call-center" | "agent-default", "clear">>;

  /**
   * Per-topic routing-group setting overrides applied for this case (see
   * `RoutingGroupEvalOverrides`). Use it to pin operator bot-conduct settings
   * — e.g. "this group requires the caller's name before a ticket is filed".
   */
  routingGroupOverrides?: RoutingGroupEvalOverrides;

  /** Per-slug call-script overrides written at the eval location for this case (see `CallScriptEvalOverrides`). */
  callScriptOverrides?: CallScriptEvalOverrides;

  /** Per-type sequence ladders written at the eval location for this case (see `SequenceLadderEvalOverrides`). */
  sequenceLadderOverrides?: SequenceLadderEvalOverrides;

  /** Per-slug step-library rows written at the eval org for this case (see `CallScriptStepEvalOverrides`). */
  callScriptStepOverrides?: CallScriptStepEvalOverrides;

  /** Ground-truth declarations the judge needs (forwarded from BaseEvalCase) */
  groundTruth?: { key: string; label: string }[];

  /**
   * Declarative policy-worksheet answers this story depends on
   * (policyKey → itemKey → value). At seed time the eval fixture pipeline
   * writes these as org-scope `policy_worksheet` rows (mode "generate") on
   * the eval org — same declarative philosophy as `locationConfig`/
   * `groundTruth` (no topup scripts). Role scoping follows the same
   * convention as `groundTruth`: the declaration buckets under this spec's
   * `locationFixture` role, though rows land at ORG scope, so declarations
   * for the same policyKey must agree across stories/roles (the seeder
   * fails loudly on conflicts).
   *
   * Absence = platform defaults: a story that needs a policy at its
   * platform-default answers declares NOTHING here — generated policy text
   * always exists via the definition's `defaultValue`s, no row required.
   * (An entry with an empty `{}` answers map is treated the same way.)
   * Only declare the itemKeys whose values the story's criteria actually
   * pin. See docs/snowy/plans/2026-07-15-policy-builder-bot-cutover.md §4.
   */
  policyAnswers?: Record<string, Record<string, string>>;

  /**
   * Sequence-context priming: assistant message that was sent before the
   * user's first reply. Injected into the bot's message history before
   * turn 1 fires, so the bot's response is anchored to that prior outbound.
   * See `PriorOutboundContext` jsdoc.
   */
  priorOutboundContext?: PriorOutboundContext;

  /**
   * Widget-config projection injected onto the eval conversation at create
   * time. Drives M0b focus + group-lock prefill (see WidgetEvalConfig jsdoc).
   * Production code paths pass a real WidgetInterface; eval cases that need
   * to exercise group-locked or location-pinned widget behavior set the
   * relevant subset of fields here.
   */
  widgetConfig?: WidgetEvalConfig;

  /**
   * Symbolic eval-org group selector. Resolved at run time against
   * `ResolvedFixtures.groups` (DataCopier creates a primary + secondary
   * group in the eval-org). When set AND `widgetConfig.groupId` is not
   * already a real number, the harness substitutes the resolved group id
   * before forwarding to ChatConversationService.create. Use this
   * for M0b group-lock stories — declared statically in the story but
   * resolved to per-run-fresh ids.
   */
  widgetGroupFixture?: "primary" | "secondary";
}

/**
 * Read-back summary of the most recent eval run for a (story × channel).
 * Populated by the eval runner on completion (dev-only — see Phase 2 §P2.D).
 * Studio uses this to show last-eval badges in the list view without joining
 * `bot_eval_run` per row.
 */
export interface UserStoryLastEvalSummary {
  /** `bot_eval_run.id` */
  runId: number;
  passed: boolean;
  /** Weighted score 0-100 */
  score: number;
  /** ISO timestamp the run completed */
  ranAt: string;
}

/**
 * Aggregated dependencies observed across recent eval runs. Drives Cortex
 * requirement derivation — "to support this story, you need these vars/tools/prompts."
 * Refreshed on every successful run; data captured via the `evalCapture` ALS
 * hook (see `apps/bot/src/Evals/EvalCaptureContext.ts`).
 */
export interface UserStoryObservedDependencies {
  /** Variable keys (LocationVarKey / OrgVarKey / etc.) the bot read during runs. */
  variableKeys: string[];
  /** Tool names invoked (e.g., "find_locations", "list_units"). */
  toolNames: string[];
  /** System-prompt section ids that were active. */
  promptSectionIds: string[];
  /** ISO timestamp this snapshot was last refreshed. */
  capturedAt: string;
}

/**
 * UserStory — the unified representation of a Snowy user story + its (optional)
 * eval test case. Stories live as TypeScript files under
 * `libs/types/src/UserStories/<Domain>/index.ts`. Studio's
 * /ai-studio/<domain>/user-stories page edits them; the server writes back
 * to the source files via pure-regen + prettier.
 *
 * See plan: docs/snowy/plans/2026-04-30-user-story-eval-management.md.
 */
export interface UserStory {
  /**
   * Stable id, e.g. `us-001-find-locations-by-zip` (legacy with slug) or
   * `us-0214` (new format, server-generated). The leading numeric portion
   * after `us-` is what drives ordering — extract via `parseStoryNumber(id)`.
   *
   * New IDs are auto-assigned by the server on first save (don't pass one
   * from the studio Add form). Existing IDs are preserved as-is.
   */
  id: string;

  domain: Domain;

  /**
   * Category groups stories within a domain (e.g. "Locations",
   * "Units, sizing, and pricing"). Determines sort order via STORAGE_CATEGORIES.
   * Changing category is a metadata mutation on the same file.
   */
  category: string;

  title: string;
  description: string;

  // ─── Per-channel state ─────────────────────────────────────────────────────

  /**
   * Per-channel lifecycle status. Channels NOT present in the map = the story
   * doesn't apply to that channel (it isn't run there at all). Replaces the
   * earlier `status` + `channels[]` pair.
   *
   * - `backlog`: not committed; eval failures silenced (aspirational, not regressions)
   * - `building`: actively under construction; eval failures DO count
   * - `live`: shipped + eval passing; failures are regressions
   * - `deprecated`: retired; skipped
   */
  channelStatus: Partial<Record<BotPromptChannel, UserStoryStatus>>;

  // ─── Collaboration metadata (slim) ─────────────────────────────────────────

  /** Email of the person who first created the story. Auto-stamped by the server on first save. */
  createdBy?: string;
  /** ISO timestamp of first save. Auto-stamped by the server. */
  createdAt?: string;
  /** Free-form markdown notes — design rationale, edge cases, blockers. */
  notes?: string;

  // ─── Tracking ──────────────────────────────────────────────────────────────

  /**
   * ISO timestamp of the most recent edit. The save endpoint sets this; the
   * studio frontend uses it (with localStorage `lastSeenAt` per user) to show
   * "new since you last looked" pills.
   */
  updatedAt: string;

  // ─── The optional embedded eval ────────────────────────────────────────────

  /**
   * Shared eval spec — applies to every channel in `channelStatus` UNLESS
   * `evalByChannel[c]` overrides it. Stories without any eval (neither shared
   * nor per-channel) are scoping/design entries and don't run.
   */
  eval?: UserStoryEvalSpec;

  /**
   * Per-channel eval-spec overrides. Merge semantics: when running channel
   * `c`, the harness uses `evalByChannel?.[c]` if defined, else `eval`.
   * Channel-specific criteria like "On voice, bot reads at most two locations"
   * should move here instead of being smuggled into shared criteria prose.
   */
  evalByChannel?: Partial<Record<BotPromptChannel, UserStoryEvalSpec>>;

  /**
   * Browser-level E2E eval spec — sibling to `eval`. Used for widget-rendering
   * behaviors the conversation eval can't observe (transcript collapse,
   * picker_api_call events, client-side network calls). Driven by a chrome-
   * devtools MCP agent per `docs/snowy/guides/e2e-protocol.md`, NOT a CI
   * process. The conversation-eval pipeline ignores this field.
   *
   * Plan: docs/snowy/plans/2026-05-10-storage-stories-fix-plan.md §B2.
   */
  e2eEval?: E2EEvalSpec;

  // ─── Observability (populated by eval runs, dev-only write-back) ───────────

  /** Last-eval result per channel. Server populates after each run. */
  lastEval?: Partial<Record<BotPromptChannel, UserStoryLastEvalSummary>>;

  /** Aggregated vars/tools/prompts observed across recent runs. */
  observedDependencies?: UserStoryObservedDependencies;
}

/**
 * Map UserStoryStatus → FeatureStatus for the eval harness.
 *
 * `building` and `live` are "real" — eval failures count. `backlog` and
 * `deprecated` get the silenced treatment.
 */
export type FeatureStatusForUserStory = "live" | "building" | "backlog";

export function toFeatureStatus(s: UserStoryStatus): FeatureStatusForUserStory {
  if (s === "live") return "live";
  if (s === "building") return "building";
  return "backlog";
}

/**
 * True iff a failure on a case with this status should count as a real
 * failure (i.e. break the suite signal). The harness uses this everywhere
 * it splits "failed" from "expectedFailure".
 */
export function isFailureMeaningful(s: UserStoryStatus): boolean {
  return s === "live" || s === "building";
}

/**
 * Extract the numeric portion of a story id for sorting + display.
 *
 * Accepts both legacy slugged ids (`us-001-find-locations-by-zip`) and the
 * new auto-generated format (`us-0214`). Returns 0 if the id doesn't match
 * the expected `us-NNNN` prefix (shouldn't happen — IDs are validated on
 * save).
 */
export function parseStoryNumber(id: string): number {
  const m = /^us-(\d+)/.exec(id);
  return m ? parseInt(m[1], 10) : 0;
}

/**
 * Tools recognized as referenced by a single criterion. A tool counts as
 * referenced if EITHER:
 *   1. The criterion has an explicit `toolName` field AND its `type` is one
 *      of TOOL_NAMED_CRITERION_TYPES (`tool_called`, `tool_not_called`, etc.) —
 *      this is the structured form authors use today.
 *   2. The criterion's `check` text mentions any tool name from
 *      KNOWN_TOOL_NAMES via case-insensitive substring match — covers prose
 *      criteria like "Bot calls escalate_to_team and ...".
 *
 * Used by the studio user-stories list page to filter by tool, and by the
 * story-detail panel to show a read-only tool-chip summary so reviewers can
 * see at a glance which tools a story exercises.
 */
function collectToolsFromCriteria(criteria: EvalCriterion[]): Set<string> {
  const out = new Set<string>();
  const toolNamedTypes = new Set<string>(TOOL_NAMED_CRITERION_TYPES);
  for (const c of criteria) {
    if (c.toolName && c.type && toolNamedTypes.has(c.type)) {
      out.add(c.toolName);
    }
    const lowered = c.check.toLowerCase();
    for (const tool of KNOWN_TOOL_NAMES) {
      if (lowered.includes(tool)) out.add(tool);
    }
  }
  return out;
}

/**
 * Tools a story exercises. Walks the shared `eval.criteria` plus every
 * `evalByChannel[c].criteria` and returns the union. Returns an empty set
 * for stories with no eval (scoping/design entries).
 */
export function deriveToolUsage(story: UserStory): Set<string> {
  const out = new Set<string>();
  if (story.eval?.criteria) {
    for (const t of collectToolsFromCriteria(story.eval.criteria)) out.add(t);
  }
  if (story.evalByChannel) {
    for (const spec of Object.values(story.evalByChannel)) {
      if (spec?.criteria) {
        for (const t of collectToolsFromCriteria(spec.criteria)) out.add(t);
      }
    }
  }
  return out;
}

/**
 * The runnable shape that the eval harness expects per case. Mirrors
 * `apps/bot/src/Evals/Types/Eval.Types.ts → EvalCase` so the harness can
 * import this from `@swivl-mono/types` without depending on apps/bot.
 *
 * Note: the harness's local `EvalCase` type-aliases this for now; we may
 * eventually delete the local alias entirely.
 */
export interface RunnableEvalCase extends BaseEvalCase {
  /** Conversation turns the eval driver runs */
  turns: EvalTurn[];

  /** Location-variable overrides applied for this case */
  locationConfig?: Partial<Record<LocationVarKey, string>>;

  /** Per-slug Schedule overrides applied for this case (see UserStoryEvalSpec). */
  scheduleOverrides?: Partial<Record<"office-hours" | "gate-access" | "call-center" | "agent-default", "clear">>;

  /** Declarative policy-worksheet answers seeded as org-scope rows (see UserStoryEvalSpec). */
  policyAnswers?: Record<string, Record<string, string>>;

  /** Per-topic routing-group setting overrides applied for this case (see UserStoryEvalSpec). */
  routingGroupOverrides?: RoutingGroupEvalOverrides;

  /** Per-slug call-script overrides applied for this case (see UserStoryEvalSpec). */
  callScriptOverrides?: CallScriptEvalOverrides;

  /** Per-type sequence ladders applied for this case (see UserStoryEvalSpec). */
  sequenceLadderOverrides?: SequenceLadderEvalOverrides;

  /** Per-slug step-library rows applied for this case (see UserStoryEvalSpec). */
  callScriptStepOverrides?: CallScriptStepEvalOverrides;

  /** Which fixture location to use */
  locationFixture?: string;

  /** Which fixture user to use */
  userFixture?: string;

  /** Sequence-context priming injected before the first user turn (see UserStoryEvalSpec). */
  priorOutboundContext?: PriorOutboundContext;

  /** Widget-config projection injected at conversation create time (see UserStoryEvalSpec). */
  widgetConfig?: WidgetEvalConfig;

  /** Symbolic group-fixture selector resolved at runtime (see UserStoryEvalSpec). */
  widgetGroupFixture?: "primary" | "secondary";
}
